<template>
  <div>
    <div class="d-flex align-center mb-3">
      <span class="subtitle-1">Service Type</span>
      <v-spacer></v-spacer>
      <v-btn class="icon-button primary" @click="openCreateDialog">
        <v-icon size="32">mdi-plus</v-icon>
      </v-btn>
    </div>

    <div v-if="availableProducts.length > 0">
      <v-chip
        v-for="product in availableProducts"
        :key="product._id"
        class="ma-1"
        @click="openEditDialog(product)"
      >
        <v-icon v-if="product.mdiIcon" small left>{{ product.mdiIcon }}</v-icon>
        {{ getProductName(product) }}
        <v-icon small right>mdi-pencil</v-icon>
      </v-chip>
    </div>
    <div v-else-if="!loadingProducts" class="text--secondary">No service products available</div>
    <div v-else class="text--secondary">Loading service products...</div>

    <!-- Edit Service Product Dialog -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">{{
            isEditing ? 'Edit Service Product' : 'Create New Service Product'
          }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="newProduct.name.en"
                label="Service Name (EN)"
                outlined
                required
                :error="!newProduct.name.en && showValidation"
                :error-messages="
                  !newProduct.name.en && showValidation ? 'English name is required' : ''
                "
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="newProduct.name['zh-hk']"
                label="Service Name (ZH-HK)"
                outlined
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="newProduct.name['zh-cn']"
                label="Service Name (ZH-CN)"
                outlined
              />
            </v-col>
            <v-col cols="12">
              <div class="text-subtitle-1 mb-2">Service Icon</div>
              <div class="icon-grid-container">
                <div class="icon-grid">
                  <v-btn
                    v-for="icon in medicalIcons"
                    :key="icon"
                    icon
                    small
                    :color="newProduct.mdiIcon === icon ? 'black' : ''"
                    :outlined="newProduct.mdiIcon === icon"
                    @click="newProduct.mdiIcon = newProduct.mdiIcon === icon ? '' : icon"
                    class="ma-1"
                  >
                    <v-icon>{{ icon }}</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="isEditing"
            color="error"
            text
            :loading="deletingProduct"
            @click="confirmDeleteCurrentProduct"
          >
            Remove
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="savingProduct" @click="saveProduct">
            {{ isEditing ? 'Save' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Service Product Management Dialog -->
    <v-dialog v-model="serviceProductDialog" max-width="600">
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Manage Service Products</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="serviceProductDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="searchText"
            label="Search service products"
            prepend-inner-icon="mdi-magnify"
            outlined
            flat
            clearable
            @input="debouncedSearch"
          />

          <v-progress-linear v-if="loadingProducts" indeterminate color="primary" class="mb-3" />

          <div v-if="availableProducts.length > 0" style="max-height: 400px; overflow-y: auto">
            <v-list>
              <v-list-item v-for="product in availableProducts" :key="product._id">
                <v-list-item-icon v-if="product.mdiIcon">
                  <v-icon>{{ product.mdiIcon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ getProductName(product) }}</v-list-item-title>
                  <v-list-item-subtitle v-if="product.description">
                    {{ product.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon color="error" @click="confirmDeleteProduct(product)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
          <div v-else-if="!loadingProducts" class="text-center text--secondary py-4">
            No service products found
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="serviceProductDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api';
import { listServiceProduct, createServiceProduct } from '@/api/serviceProduct';
import { debounce } from 'lodash';

export default {
  name: 'ServiceProductManager',
  data() {
    return {
      editDialog: false,
      isEditing: false,
      editingProductId: null,
      serviceProductDialog: false,
      availableProducts: [],
      searchText: '',
      loadingProducts: false,
      savingProduct: false,
      deletingProduct: false,
      showValidation: false,
      newProduct: {
        name: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        mdiIcon: '',
      },
      medicalIcons: [
        // Medical & Healthcare
        'mdi-hospital-building',
        'mdi-hospital',
        'mdi-hospital-box',
        'mdi-medical-bag',
        'mdi-pill',
        'mdi-needle',
        'mdi-stethoscope',
        'mdi-thermometer',
        'mdi-blood-bag',
        'mdi-bandage',
        'mdi-microscope',
        'mdi-test-tube',
        'mdi-flask',
        'mdi-dna',
        'mdi-bacteria',
        'mdi-virus',

        // Body Parts & Health
        'mdi-heart',
        'mdi-heart-pulse',
        'mdi-brain',
        'mdi-stomach',
        'mdi-tooth',
        'mdi-eye',
        'mdi-ear-hearing',
        'mdi-hand-heart',
        'mdi-foot-print',
        'mdi-bone',

        // Medical Specialists
        'mdi-doctor',
        'mdi-account-injury',
        'mdi-mother-nurse',
        'mdi-baby-carriage',
        'mdi-face-woman',
        'mdi-human-pregnant',

        // Medical Equipment & Services
        'mdi-wheelchair',
        'mdi-wheelchair-accessibility',
        'mdi-ambulance',
        'mdi-clipboard-pulse',
        'mdi-clipboard-plus',
        'mdi-bottle-tonic',
        'mdi-eyedropper',
        'mdi-diabetes',
        'mdi-allergy',

        // Wellness & Care
        'mdi-meditation',
        'mdi-yoga',
        'mdi-spa',
        'mdi-emoticon-happy',
        'mdi-shield-check',
        'mdi-account-heart',

        // Common/General Icons
        'mdi-clock',
        'mdi-clock-outline',
        'mdi-phone',
        'mdi-email',
        'mdi-map-marker',
        'mdi-home',
        'mdi-office-building',
        'mdi-star',
        'mdi-star-outline',
        'mdi-check-circle',
        'mdi-information',
        'mdi-help-circle',
        'mdi-alert-circle',
        'mdi-clipboard-text',
        'mdi-file-document',
        'mdi-chat',
        'mdi-message',
        'mdi-bell',
        'mdi-cog',
        'mdi-wrench',
        'mdi-magnify',
        'mdi-plus-circle',
        'mdi-pencil',
        'mdi-content-cut',
        'mdi-content-copy',
        'mdi-content-paste',
        'mdi-tag',
        'mdi-bookmark',
        'mdi-flag',
        'mdi-thumb-up',
        'mdi-hand-okay',
        'mdi-certificate',
      ],
    };
  },
  computed: {
    debouncedSearch() {
      return debounce(this.searchProducts, 300);
    },
  },
  async mounted() {
    await this.loadServiceProducts();
  },
  methods: {
    getProductName(product) {
      if (!product || !product.name) return '';

      try {
        const name = typeof product.name === 'string' ? JSON.parse(product.name) : product.name;
        return name.en || name['zh-hk'] || name['zh-cn'] || 'Unnamed Product';
      } catch (e) {
        return product.name || 'Unnamed Product';
      }
    },

    async loadServiceProducts() {
      try {
        this.loadingProducts = true;
        const data = await listServiceProduct(api, {
          limit: 1000,
        });

        // Filter only enabled products
        const products = data?.docs || [];
        this.availableProducts = products.filter(product => product.enabled !== false);
      } catch (error) {
        console.error('Error loading service products:', error);
        this.$emit('error', 'Failed to load service products');
      } finally {
        this.loadingProducts = false;
      }
    },

    async searchProducts() {
      if (!this.searchText?.trim()) {
        await this.loadServiceProducts();
        return;
      }

      try {
        this.loadingProducts = true;
        const data = await listServiceProduct(api, {
          select: '_id name description enabled',
          search: this.searchText,
          limit: 100,
        });

        const products = Array.isArray(data) ? data : data.results || [];
        this.availableProducts = products.filter(product => product.enabled !== false);
      } catch (error) {
        console.error('Error searching service products:', error);
      } finally {
        this.loadingProducts = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.editingProductId = null;
      this.editDialog = true;
      this.showValidation = false;
      this.newProduct = {
        name: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        mdiIcon: '',
      };
    },

    openEditDialog(product) {
      this.isEditing = true;
      this.editingProductId = product._id;
      this.editDialog = true;
      this.showValidation = false;

      // Parse and populate the form with existing product data
      try {
        const name = typeof product.name === 'string' ? JSON.parse(product.name) : product.name;
        this.newProduct = {
          name: {
            en: name.en || '',
            'zh-hk': name['zh-hk'] || '',
            'zh-cn': name['zh-cn'] || '',
          },
          mdiIcon: product.mdiIcon || '',
        };
      } catch (e) {
        console.error('Error parsing product data:', e);
        this.newProduct = {
          name: {
            en: product.name || '',
            'zh-hk': '',
            'zh-cn': '',
          },
          mdiIcon: product.mdiIcon || '',
        };
      }
    },

    closeEditDialog() {
      this.editDialog = false;
      this.isEditing = false;
      this.editingProductId = null;
      this.showValidation = false;
      this.newProduct = {
        name: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        mdiIcon: '',
      };
    },

    openServiceProductDialog() {
      this.serviceProductDialog = true;
      this.searchText = '';
      this.loadServiceProducts();
    },

    async removeProduct(productId) {
      try {
        this.deletingProduct = true;
        await api.delete(`rml/admin/ServiceProducts/${productId}`);
        this.availableProducts = this.availableProducts.filter(p => p._id !== productId);
        this.$emit('success', 'Service product deleted successfully');
        this.closeEditDialog();
      } catch (error) {
        console.error('Error deleting service product:', error);
        this.$emit('error', 'Failed to delete service product');
      } finally {
        this.deletingProduct = false;
      }
    },

    confirmDeleteCurrentProduct() {
      const productName = this.newProduct.name.en || 'this product';
      this.$root.$confirm
        .open('Confirm Delete', `Are you sure you want to delete "${productName}"?`, {
          color: 'error',
        })
        .then(async confirm => {
          if (confirm) {
            await this.removeProduct(this.editingProductId);
          }
        });
    },

    confirmDeleteProduct(product) {
      this.$root.$confirm
        .open('Confirm', `Are you sure you want to delete "${this.getProductName(product)}"?`, {
          color: 'warning',
        })
        .then(async confirm => {
          if (confirm) {
            this.removeProduct(product._id);
          }
        });
    },

    async saveProduct() {
      if (!this.newProduct.name.en?.trim()) {
        this.showValidation = true;
        return;
      }

      try {
        this.savingProduct = true;

        const payload = {
          name: JSON.stringify(this.newProduct.name),
          mdiIcon: this.newProduct.mdiIcon || '',
          enabled: true,
        };

        if (this.isEditing) {
          // Update existing product
          await api.patch(`rml/admin/ServiceProducts/${this.editingProductId}`, payload);
          this.$emit('success', 'Service product updated successfully');
        } else {
          // Create new product
          const response = await createServiceProduct(api, payload);
          this.availableProducts.push(response);
          this.$emit('success', 'Service product created successfully');
        }

        // Reset form and close dialog
        this.closeEditDialog();

        // Reload the list to ensure consistency
        await this.loadServiceProducts();
      } catch (error) {
        console.error('Error saving service product:', error);
        this.$emit('error', `Failed to ${this.isEditing ? 'update' : 'create'} service product`);
      } finally {
        this.savingProduct = false;
      }
    },
  },
};
</script>

<style scoped>
.v-chip {
  margin: 2px;
}

.icon-grid-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.icon-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-start;
}

.icon-grid .v-btn {
  width: 40px;
  height: 40px;
  min-width: 40px;
}
</style>
